import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { removeSasFromDescription } from "../../Imagehelper";
import { fields } from "../constants";
import { useMyAirportsPrivacyPolicy } from "./MyAirports.hooks";

const AddEditMyAirports = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const effectiveId = query.get("editId") || query.get("id") || undefined;

  const {
    data: editData,
    error,
    isLoading: isLoadingEdit,
    isError,
    refetch: refetchEdit,
  } = useMyAirportsPrivacyPolicy();

  const {
    data: editDataById,
    isLoading: isLoadingDocument,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const recordId = editData?.id || editDataById?.payload?.id;

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    } else if (query.get("editId")) {
      refetchEdit();
    } else {
      refetchEdit();
    }
  }, [query.get("id"), query.get("editId")]);

  const formConfig: FormConfig = {
    initialValues: {
      tab_title_english_1:
        decodeHTML(editDataById?.payload?.tab_title_english_1) || decodeHTML(editData?.tab_title_english_1) || "",

      tab_title_malay_1:
        decodeHTML(editDataById?.payload?.tab_title_malay_1) || decodeHTML(editData?.tab_title_malay_1) || "",

      tab_title_mandarin_1:
        decodeHTML(editDataById?.payload?.tab_title_mandarin_1) || decodeHTML(editData?.tab_title_mandarin_1) || "",

      tab_title_english_2:
        decodeHTML(editDataById?.payload?.tab_title_english_2) || decodeHTML(editData?.tab_title_english_2) || "",

      tab_title_malay_2:
        decodeHTML(editDataById?.payload?.tab_title_malay_2) || decodeHTML(editData?.tab_title_malay_2) || "",

      tab_title_mandarin_2:
        decodeHTML(editDataById?.payload?.tab_title_mandarin_2) || decodeHTML(editData?.tab_title_mandarin_2) || "",

      description_english_tab_1:
        decodeHTML(editDataById?.payload?.description_english_tab_1) ||
        decodeHTML(editData?.description_english_tab_1) ||
        "",

      description_malay_tab_1:
        decodeHTML(editDataById?.payload?.description_malay_tab_1) ||
        decodeHTML(editData?.description_malay_tab_1) ||
        "",

      description_mandarin_tab_1:
        decodeHTML(editDataById?.payload?.description_mandarin_tab_1) ||
        decodeHTML(editData?.description_mandarin_tab_1) ||
        "",

      description_english_tab_2:
        decodeHTML(editDataById?.payload?.description_english_tab_2) ||
        decodeHTML(editData?.description_english_tab_2) ||
        "",

      description_malay_tab_2:
        decodeHTML(editDataById?.payload?.description_malay_tab_2) ||
        decodeHTML(editData?.description_malay_tab_2) ||
        "",

      description_mandarin_tab_2:
        decodeHTML(editDataById?.payload?.description_mandarin_tab_2) ||
        decodeHTML(editData?.description_mandarin_tab_2) ||
        "",
    },
    fields: fields.myAirportsPrivacyPolicy,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let descriptionKeys: any = [
      "description_english_tab_1",
      "description_malay_tab_1",
      "description_mandarin_tab_1",
      "description_english_tab_2",
      "description_malay_tab_2",
      "description_mandarin_tab_2",
    ];
    for (const key of descriptionKeys) {
      if (data[key]) {
        data[key] = removeSasFromDescription(data[key]);
      }
    }
    const payload = {
      action: action,
      method: method,
      module: "Centralized Policies",
      subModule: "myAirportsPrivacyPolicy",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "CENTRALIZED",
          apiUrl: apiUrl,
        },
      ],
      // moduleKey: "CENTRALIZED_MyAirportsPrivacyPolicy",
      payload: {
        ...(action === "UPDATE" && { id: editData?.id || editDataById?.payload?.id }),
        ...data,
        created_by: action === "UPDATE" ? editData?.created_by || editDataById?.payload?.created_by : undefined,
        last_modified_by: action === "UPDATE" ? user?.email : null,
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <>
      <DynamicForm
        key={recordId || "create"}
        isLoading={isLoadingEdit || isLoadingDocument}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            recordId
              ? `${centralizedHost}/api/my-airports-privacy-policy/${recordId}`
              : `${centralizedHost}/api/my-airports-privacy-policy`,
            recordId ? "UPDATE" : "CREATE",
            recordId ? "PATCH" : "POST",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Policies"
      />
    </>
  );
};

export default AddEditMyAirports;
